/*
 * ////////////////////////////////////////////////////////////////////////////////
 * //
 * // This software system consists of computer software and documentation.
 * // It contains trade secrets and confidential information which are proprietary
 * // to Everi Games Inc.  Its use or disclosure in whole or in part without
 * // the express written permission of Everi Games Inc. is prohibited.
 * //
 * // This software system is also an unpublished work protected under the copyright
 * // laws of the United States of America.
 * //
 * // Copyright © 2020 Everi Games Inc.  All Rights Reserved
 * //
 * ////////////////////////////////////////////////////////////////////////////////
 */
import ComponentBase from '../../ComponentBase';
import Decorators from '../../Decorators';
import { within } from '../../Misc';
import { NodeEvent } from './NodeEvent';

/** @hidden */
const property: typeof cc._decorator.property = cc._decorator.property;

@cc._decorator.ccclass
@cc._decorator.executeInEditMode
export default class AutoScalingNode extends ComponentBase
{
    @property(cc.Node)
    protected target: cc.Node = null;

    @property
    protected fitWidth: boolean = true;

    @property
    protected fitHeight: boolean = false;

    @property
    protected keepAspectRatio: boolean = true;

    @property(cc.Float)
    protected delaySs: number = 0.05;

    @property(cc.Vec2)
    protected minScale: cc.Vec2 = new cc.Vec2(0.1, 0.1);

    @property(cc.Vec2)
    protected maxScale: cc.Vec2 = new cc.Vec2(1, 1);

    private _lastUpdate: number = 0;
    private _isBusy: boolean = false;

    /** @hidden */
    protected start(): void
    {
        super.start();
        this.setupTarget();

        this.sizeChangedHandler();
        this.onNodeEvent(NodeEvent.SIZE_CHANGED, this.sizeChangedHandler);
        this.onNodeEvent(NodeEvent.CHILD_ADDED, this.sizeChangedHandler);
        this.onNodeEvent(NodeEvent.CHILD_REMOVED, this.sizeChangedHandler);
        this.onTargetEvent(this.node.parent, NodeEvent.SIZE_CHANGED, this.sizeChangedHandler);
    }

    /** @hidden */
    @Decorators.BindThis()
    private sizeChangedHandler(): void
    {
        const count: number = ++this._lastUpdate;
        this.scheduleOnce(() =>
        {
            if (count !== this._lastUpdate
                || this._isBusy
                || !this.target) { return; }
            this._isBusy = true;

            this.widthChangedHandler();
            this.heightChangedHandler();

            this._isBusy = false;
        }, this.delaySs);
    }

    /** @hidden */
    private widthChangedHandler(): void
    {
        if (!this.fitWidth) { return; }

        const size: cc.Size = this.node.getContentSize();
        if (size.width === 0) { return; }

        const targetSize: cc.Size = this.target.getContentSize();

        let newScale: number = targetSize.width / size.width;
        newScale = within(this.minScale.x, this.maxScale.x, newScale);

        this.node.scaleX = newScale;
        if (this.keepAspectRatio) { this.node.scaleY = newScale; }
    }

    /** @hidden */
    private heightChangedHandler(): void
    {
        if (!this.fitHeight) { return; }

        const size: cc.Size = this.node.getContentSize();
        if (size.height === 0) { return; }

        const targetSize: cc.Size = this.target.getContentSize();

        let newScale: number = targetSize.height / size.height;
        newScale = within(this.minScale.y, this.maxScale.y, newScale);

        this.node.scaleY = newScale;
        if (this.keepAspectRatio) { this.node.scaleY = newScale; }
    }

    /** @hidden */
    private setupTarget(): void
    {
        if (this.target) { return; }
        this.target = this.node.parent;
    }
}
